import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, type PackingList, type TripRow } from '../api.js';
import { Button, Card, Empty, ErrorBox, Loading, Screen, slotLabel, tripStatusLabel} from '../ui.js';

/**
 * §9 Reise-/Packmodus.
 *
 * Der eigentliche Zweck ist nicht die Liste, sondern die **Reservierung**: eingepackte
 * Teile stehen zu Hause nicht mehr zur Verfügung und tauchen in keinem Vorschlag mehr
 * auf. Ohne das würde die Morgens-Ansicht ein Hemd empfehlen, das im Koffer liegt —
 * genau die Sorte falscher Zustand, die schlimmer ist als ein fehlender.
 */
export function Trips({ navigate }: { navigate: (to: string) => void }): ReactNode {
  const trips = useQuery({ queryKey: ['trips'], queryFn: api.trips.list });
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  if (trips.isLoading) return <Loading />;
  const list = trips.data ?? [];
  const active = list.filter((t) => t.status === 'planned' || t.status === 'active');
  const past = list.filter((t) => t.status === 'done' || t.status === 'cancelled');

  return (
    <Screen title="Reisen" action={<Button variant="ghost" onClick={() => navigate('/mehr')}>Zurück</Button>}>
      <ErrorBox error={trips.error} />

      {creating ? (
        <NewTrip
          onDone={(id) => {
            setCreating(false);
            setOpenId(id);
            void trips.refetch();
          }}
          onCancel={() => setCreating(false)}
        />
      ) : (
        <Button className="mb-4 w-full" onClick={() => setCreating(true)}>
          Neue Reise anlegen
        </Button>
      )}

      {active.length === 0 && !creating && (
        <Empty>
          Keine geplante Reise. Eine Reise reserviert Teile, damit sie zu Hause nicht mehr
          vorgeschlagen werden.
        </Empty>
      )}

      <div className="space-y-3">
        {active.map((trip) => (
          <TripCard
            key={trip.id}
            trip={trip}
            open={openId === trip.id}
            onToggle={() => setOpenId(openId === trip.id ? null : trip.id)}
            onChanged={() => void trips.refetch()}
          />
        ))}
      </div>

      {past.length > 0 && (
        <>
          <p className="mt-6 mb-2 text-xs text-white/45">Abgeschlossen</p>
          <div className="space-y-2">
            {past.map((trip) => (
              <Card key={trip.id} className="opacity-60">
                <p className="text-sm">{trip.name}</p>
                <p className="text-xs text-white/45">
                  {trip.start_date} bis {trip.end_date} · {tripStatusLabel(trip.status)}
                </p>
              </Card>
            ))}
          </div>
        </>
      )}
      <div className="h-8" />
    </Screen>
  );
}

function NewTrip({ onDone, onCancel }: { onDone: (id: string) => void; onCancel: () => void }): ReactNode {
  const today = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [laundry, setLaundry] = useState(false);
  const [coords, setCoords] = useState('');

  const create = useMutation({
    mutationFn: () => {
      const [lat, lon] = coords.split(',').map((s) => Number(s.trim()));
      return api.trips.create({
        name: name.trim(),
        startDate,
        endDate,
        laundryAvailable: laundry,
        destLat: coords !== '' && !Number.isNaN(lat) ? lat : null,
        destLon: coords !== '' && !Number.isNaN(lon) ? lon : null,
        status: 'planned',
      });
    },
    onSuccess: (res) => onDone(res.id),
  });

  return (
    <Card className="mb-4">
      <p className="text-sm font-medium">Neue Reise</p>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Wohin?"
        className="mt-3 w-full rounded-xl bg-white/6 px-3 py-2 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
      />
      <div className="mt-2 flex gap-2">
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="flex-1 rounded-xl bg-white/6 px-3 py-2 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
        />
        <input
          type="date"
          value={endDate}
          min={startDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="flex-1 rounded-xl bg-white/6 px-3 py-2 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
        />
      </div>
      <input
        value={coords}
        onChange={(e) => setCoords(e.target.value)}
        placeholder="Ziel-Koordinaten, z. B. 45.4642, 9.1900 (optional)"
        className="mt-2 w-full rounded-xl bg-white/6 px-3 py-2 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
      />
      <p className="mt-1 text-[11px] text-white/30">
        Mit Koordinaten rechnet die Packliste mit dem Wetter am Ziel statt mit einer Annahme.
      </p>

      <label className="mt-3 flex items-center gap-2 text-sm text-white/75">
        <input type="checkbox" checked={laundry} onChange={(e) => setLaundry(e.target.checked)} />
        Waschgelegenheit vorhanden
      </label>

      <div className="mt-4 flex gap-2">
        <Button
          className="flex-1"
          disabled={name.trim() === '' || create.isPending || endDate < startDate}
          onClick={() => create.mutate()}
        >
          Anlegen
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Abbrechen
        </Button>
      </div>
      <ErrorBox error={create.error} />
    </Card>
  );
}

function TripCard({ trip, open, onToggle, onChanged }: {
  trip: TripRow;
  open: boolean;
  onToggle: () => void;
  onChanged: () => void;
}): ReactNode {
  const qc = useQueryClient();
  const [assumed, setAssumed] = useState('');
  const detail = useQuery({
    queryKey: ['trip', trip.id],
    queryFn: () => api.trips.get(trip.id),
    enabled: open,
  });
  const list = useQuery({
    queryKey: ['packing-list', trip.id, assumed],
    queryFn: () => api.trips.packingList(trip.id, assumed === '' ? undefined : Number(assumed)),
    enabled: open,
    retry: 0,
  });

  const reserve = useMutation({
    mutationFn: (itemIds: string[]) => api.trips.reserve(trip.id, itemIds),
    onSuccess: () => {
      void detail.refetch();
      // Reservierte Teile verschwinden aus dem verfügbaren Bestand — beides muss neu.
      void qc.invalidateQueries({ queryKey: ['items'] });
      void qc.invalidateQueries({ queryKey: ['recommend'] });
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: string) => api.trips.setStatus(trip.id, status),
    onSuccess: () => {
      onChanged();
      void qc.invalidateQueries({ queryKey: ['items'] });
      void qc.invalidateQueries({ queryKey: ['recommend'] });
    },
  });

  const reserved = detail.data?.items ?? [];

  return (
    <Card>
      <button className="w-full text-left" onClick={onToggle}>
        <p className="text-sm font-medium">{trip.name}</p>
        <p className="text-xs text-white/45">
          {trip.start_date} bis {trip.end_date}
          {trip.laundry_available === 1 ? ' · Waschgelegenheit' : ''}
          {reserved.length > 0 ? ` · ${reserved.length} Teile reserviert` : ''}
        </p>
      </button>

      {open && (
        <div className="mt-4 border-t border-white/10 pt-4">
          {list.isLoading && <Loading label="Packliste wird gerechnet …" />}

          {list.error !== null && (
            <div>
              <ErrorBox error={list.error} />
              <div className="mt-2 flex gap-2">
                <input
                  type="number"
                  value={assumed}
                  onChange={(e) => setAssumed(e.target.value)}
                  placeholder="angenommene °C"
                  className="flex-1 rounded-xl bg-white/6 px-3 py-2 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
                />
                <Button variant="ghost" onClick={() => void list.refetch()}>
                  Neu rechnen
                </Button>
              </div>
            </div>
          )}

          {list.data !== undefined && <PackingListView list={list.data} />}

          <div className="mt-4 flex flex-col gap-2">
            <Button
              disabled={list.data === undefined || reserve.isPending}
              onClick={() => reserve.mutate((list.data?.entries ?? []).map((e) => e.itemId))}
            >
              {reserved.length > 0 ? 'Reservierung aktualisieren' : 'Teile reservieren'}
            </Button>
            {reserved.length > 0 && (
              <Button variant="ghost" onClick={() => reserve.mutate([])}>
                Reservierung aufheben
              </Button>
            )}
            <Button variant="ghost" onClick={() => setStatus.mutate('done')}>
              Reise abschließen (gibt alle Teile frei)
            </Button>
          </div>
          <ErrorBox error={reserve.error ?? setStatus.error} />
        </div>
      )}
    </Card>
  );
}

function PackingListView({ list }: { list: PackingList }): ReactNode {
  const bySlot = new Map<string, typeof list.entries>();
  for (const entry of list.entries) {
    bySlot.set(entry.slot, [...(bySlot.get(entry.slot) ?? []), entry]);
  }

  return (
    <div>
      {list.notes.map((note) => (
        <p key={note} className="text-xs text-white/55">
          {note}
        </p>
      ))}
      {list.weatherSource === 'angenommen' && (
        <p className="mt-2 rounded-lg bg-[#ff9f0a]/12 px-3 py-2 text-xs text-[#ffc266]">
          Ohne echte Wetterdaten gerechnet — die Liste ist nur so gut wie die Annahme.
        </p>
      )}

      <div className="mt-3 space-y-3">
        {[...bySlot.entries()].map(([slot, entries]) => (
          <div key={slot}>
            <p className="text-xs text-white/45">
              {slotLabel(slot)} · {entries[0]?.reason}
            </p>
            <ul className="mt-1 space-y-0.5 text-sm">
              {entries.map((e) => (
                <li key={e.itemId} className="text-white/85">
                  {e.name}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {list.missing.length > 0 && (
        <div className="mt-3 rounded-lg bg-[#ff453a]/12 px-3 py-2 text-xs text-[#ff9f9a]">
          <p className="font-medium">Nicht abgedeckt:</p>
          {list.missing.map((m) => (
            <p key={m}>{m}</p>
          ))}
        </div>
      )}
    </div>
  );
}

